import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FileText, Globe, Loader2, Sparkles } from "lucide-react";
import { useCvTemplates } from "@/hooks/useCvTemplates";
import { useCvGeneration } from "@/hooks/useCvGeneration";
import { useCandidateExperiences } from "@/hooks/candidate/useCandidateExperiences";
import { Candidate } from "@/types/candidate";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";

interface GenerateCvDialogProps {
	isOpen: boolean;
	onClose: () => void;
	candidate: Candidate;
	onGenerated?: () => void;
}

export function GenerateCvDialog({ isOpen, onClose, candidate, onGenerated }: GenerateCvDialogProps) {
	const { t } = useTranslation();
	const { currentOrganization } = useAuth();
	const { templates, loading: templatesLoading } = useCvTemplates();
	const { generating, progress, generateCv } = useCvGeneration();
	const { experiences, skills, loading: experiencesLoading } = useCandidateExperiences(candidate.id);

	const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
	const [polishWithAi, setPolishWithAi] = useState(true);
	const [language, setLanguage] = useState<string>("en");

	// Set default template when templates load
	useEffect(() => {
		if (templates.length > 0 && !selectedTemplateId) {
			const defaultTemplate = templates.find(t => t.is_default) || templates[0];
			setSelectedTemplateId(defaultTemplate.id);
		}
	}, [templates, selectedTemplateId]);

	const handleGenerate = async () => {
		if (!selectedTemplateId) return;

		try {
			await generateCv({
				templateId: selectedTemplateId,
				candidate,
				experiences,
				skills,
				organizationName: currentOrganization?.name,
				polishWithAi,
				language,
			});
			onGenerated?.();
			onClose();
		} catch {
			// Error handled in hook
		}
	};

	const handleClose = () => {
		if (!generating) {
			setSelectedTemplateId("");
			onClose();
		}
	};

	const isLoading = templatesLoading || experiencesLoading;

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[500px]" aria-describedby="generate-cv-description">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5" />
						{t("cvGeneration.title", "Generate CV")}
					</DialogTitle>
					<p id="generate-cv-description" className="text-sm text-muted-foreground">
						{t("cvGeneration.selectTemplate", "Select a template")} — {candidate.name}
					</p>
				</DialogHeader>

				{isLoading ? (
					<div className="flex justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-digger-blue"></div>
					</div>
				) : templates.length === 0 ? (
					<div className="text-center py-8">
						<FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
						<p className="text-sm text-muted-foreground">
							{t("cvGeneration.noTemplates", "No CV templates available. Create one in Admin Console.")}
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{/* Template Selection */}
						<div className="space-y-2">
							<Label>{t("cvGeneration.selectTemplate", "Select a template")}</Label>
							<Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
								<SelectTrigger>
									<SelectValue placeholder={t("cvGeneration.selectTemplate", "Select a template")} />
								</SelectTrigger>
								<SelectContent>
									{templates.map(template => (
										<SelectItem key={template.id} value={template.id}>
											<div className="flex items-center gap-2">
												{template.name}
												{template.is_default && (
													<Badge variant="secondary" className="text-xs">Default</Badge>
												)}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Language */}
						<div className="space-y-2">
							<Label className="flex items-center gap-1.5">
								<Globe className="h-3.5 w-3.5" />
								{t("cvGeneration.language", "Language")}
							</Label>
							<Select value={language} onValueChange={setLanguage}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="en">English</SelectItem>
									<SelectItem value="fr">Fran&#231;ais</SelectItem>
									<SelectItem value="nl">Nederlands</SelectItem>
									<SelectItem value="de">Deutsch</SelectItem>
									<SelectItem value="es">Espa&#241;ol</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{/* AI Polish */}
						<div className="flex items-center justify-between p-3 bg-muted/50 rounded-sm border">
							<div className="flex items-center gap-2">
								<Sparkles className={`h-4 w-4 ${polishWithAi ? "text-amber-500" : "text-muted-foreground"}`} />
								<div>
									<p className="text-sm font-medium">
										{t("cvGeneration.polishWithAi", "Polish with AI")}
									</p>
									<p className="text-xs text-muted-foreground">
										{t("cvGeneration.polishWithAiDescription", "AI will write experience descriptions and improve the summary")}
									</p>
								</div>
							</div>
							<Switch
								checked={polishWithAi}
								onCheckedChange={setPolishWithAi}
								disabled={generating}
							/>
						</div>

						{/* Data summary */}
						<div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
							<p>{experiences.length} experience(s), {skills.length} skill(s) available for this candidate.</p>
						</div>

						{/* Progress indicator */}
						{generating && progress && (
							<div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-sm border border-blue-200 dark:border-blue-800">
								<Loader2 className="h-4 w-4 animate-spin text-blue-500" />
								<span className="text-sm text-blue-700 dark:text-blue-300">{progress}</span>
							</div>
						)}
					</div>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={handleClose} disabled={generating}>
						{t("cvGeneration.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={generating || !selectedTemplateId || templates.length === 0}
						className="bg-digger-blue hover:bg-digger-blue-light"
					>
						{generating ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								{t("cvGeneration.generating", "Generating...")}
							</>
						) : (
							<>
								<FileText className="mr-2 h-4 w-4" />
								{t("cvGeneration.generate", "Generate")}
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
